import { type ElementType, forwardRef, type ReactElement, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { AppBar as MaterialAppBar, Container, IconButton, Toolbar, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import ConditionalWrapper from '@DS/components/components/utils/wrapper/ConditionalWrapper'
import LinkButton from '@DS/components/forms/links/linkButton/LinkButton'
import { useIsLargeUp } from '@Hooks/useResponsive'
import { svgLazyLoader } from '@Utils/icons'

import * as styles from './styles'

const ArrowBackIcon = svgLazyLoader('arrow_back')

type CloseAction =
  | {
      isInModal: true
      onCloseButtonClick?: VoidFunction
      actionLabel?: string
    }
  | {
      isInModal?: false
      onCloseButtonClick?: never
      actionLabel?: never
    }

export type AppBarProps = CloseAction & {
  children?: ReactElement
  onBackButtonClick?: VoidFunction
  title: ReactNode
  withBookingForm?: boolean
}

const AppBar = forwardRef<HTMLDivElement, AppBarProps>(
  (
    { actionLabel, children, isInModal = false, onBackButtonClick, onCloseButtonClick, title, withBookingForm = false },
    ref,
  ) => {
    const { formatMessage } = useIntl()
    const theme = useTheme()
    const isLargeUp = useIsLargeUp(theme)

    const getVariantToApply = () => {
      if (isInModal) {
        return 'h3'
      }

      return isLargeUp ? 'h2' : 'h3'
    }

    return (
      <MaterialAppBar
        css={children ? styles.appBarProminent(theme, isInModal) : styles.appBar(theme, isInModal)}
        color="primary"
        elevation={0}
        position="sticky"
        ref={ref}
      >
        <ConditionalWrapper
          condition={!isInModal}
          wrapper={(wrapperChildren) => (
            <Container maxWidth="xl" css={styles.appBarContainer(theme)}>
              <>{wrapperChildren}</>
            </Container>
          )}
        >
          <>
            <Toolbar css={styles.toolBar(theme)}>
              {onBackButtonClick && (
                <IconButton
                  aria-label={formatMessage({ id: 'appBar_backButton_ariaLabel' })}
                  color="inherit"
                  css={[styles.backButton(theme), withBookingForm && styles.backButtonWithBookingForm]}
                  data-test="back-button"
                  disableFocusRipple
                  edge="start"
                  onClick={onBackButtonClick}
                >
                  <ArrowBackIcon css={styles.arrowIcon(theme)} />
                </IconButton>
              )}
              <Typography
                css={styles.content}
                variant={getVariantToApply()}
                component={'h1' as ElementType}
                id={isInModal ? 'modal-header-title' : undefined}
              >
                {title}
              </Typography>
              {isInModal && onCloseButtonClick && (
                <LinkButton
                  onClick={onCloseButtonClick}
                  css={styles.appBarAction(theme)}
                  aria-label={formatMessage({ id: 'close_dialog_ariaLabel' })}
                  surface="onPrimary"
                >
                  {actionLabel || <FormattedMessage id="close" />}
                </LinkButton>
              )}
            </Toolbar>
            {children && <div css={styles.children(theme)}>{children}</div>}
          </>
        </ConditionalWrapper>
      </MaterialAppBar>
    )
  },
)

export default AppBar
